import { useEffect } from 'react';
import { UserType } from '@bulthaup/api/db';
import { AppShell, Badge, Burger, Divider, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconClipboardList,
  IconFileInvoice,
  IconLogout,
  IconSettings,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';

import { NavLink } from '../components/nav-link';
import { Reminder } from '../components/reminder';
import { useUser } from '../hooks/use-user';
import { trpc } from '../services/trpc';

function AppLayout() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [opened, { toggle }] = useDisclosure(false);
  const logout = trpc.auth.logout.useMutation();

  useEffect(() => {
    if (!user) {
      navigate({
        to: '/login',
        search: {
          redirect: '',
        },
      });
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 200, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header style={{ overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url('/nav.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'left',
            animation: 'moveHeaderBackground 60s ease-out',
            zIndex: 0,
          }}
        />
        <Group
          h="100%"
          px="md"
          style={{ position: 'relative', zIndex: 1 }}
          justify="space-between"
        >
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <div
              onClick={() => navigate({ to: '/app/projets' })}
              style={{ cursor: 'pointer' }}
            >
              <img
                src="/bulthaup.png"
                alt="Bulthaup logo"
                height={30}
                style={{ maxHeight: 30 }}
              />
            </div>
            {window.location.hostname === 'bulthaup-paris.com' ? (
              <Badge color="blue" size="lg">
                Prod
              </Badge>
            ) : (
              <Badge color="orange" size="lg">
                Sandbox
              </Badge>
            )}
          </Group>
          <Reminder />
        </Group>
        <style>
          {`
            @keyframes moveHeaderBackground {
              0% {
                transform: scale(1.3) translateX(120px);
              }
              100% {
                transform: scale(1) translateX(0px);
              }
            }
          `}
        </style>
      </AppShell.Header>
      <AppShell.Navbar p="md" style={{ justifyContent: 'space-between' }}>
        <Stack gap="xs">
          <NavLink
            item={{
              to: '/app/projets',
              label: 'Projets',
              icon: IconClipboardList,
            }}
            onClick={toggle}
          />
        </Stack>
        <Stack gap="xs">
          <Divider />
          <NavLink
            item={{ to: '/app/profil', label: 'Profil', icon: IconUserCircle }}
            onClick={toggle}
          />
          {user?.type === UserType.ADMIN && (
            <>
              <NavLink
                item={{
                  to: '/app/configuration',
                  label: 'Configuration',
                  icon: IconSettings,
                }}
                onClick={toggle}
              />
              <NavLink
                item={{
                  to: '/app/factures',
                  label: 'Factures',
                  icon: IconFileInvoice,
                }}
                onClick={toggle}
              />
              <NavLink
                item={{
                  to: '/app/utilisateurs',
                  label: 'Utilisateurs',
                  icon: IconUsers,
                }}
                onClick={toggle}
              />
            </>
          )}
          <NavLink
            item={{
              label: 'Se déconnecter',
              icon: IconLogout,
            }}
            onClick={async () => {
              await logout.mutateAsync();
            }}
          />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <div className="mx-auto">
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}

export const Route = createFileRoute('/app')({
  component: AppLayout,
  beforeLoad: async ({ context, location }) => {
    if (!context.user) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
        replace: true,
      });
    }
    if (['/app', '/app/'].includes(location.pathname)) {
      throw redirect({
        to: '/app/projets',
        replace: true,
      });
    }
  },
});
